import { FuscaVehiclesRequester } from '@cobliteam/api-lib';
import GrahpQLVehicleRequester from '@cobliteam/api-lib/src/dashboard/vehicle/GrahpQLVehicleRequester';
import type { AssociationPastRequest, NewVehicleForm } from '@cobliteam/api-lib/src/dashboard/vehicle/Types';
import type { UUID, Vehicle } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY as DEVICES_QUERY_KEY } from '../useDevices';
import useUser from '../useUser';
import { QUERY_KEY as VEHICLES_QUERY_KEY } from '../useVehicle';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getFuscaVehiclesRequester = () =>
  new FuscaVehiclesRequester({
    host: env.apiHost,
  });
const getVehicleGraphQLRequester = () => new GrahpQLVehicleRequester();

const QUERY_KEY = [VEHICLES_QUERY_KEY];

const useFuscaVehicle = () => {
  const queryClient = useQueryClient();
  const { useGetUserData } = useUser();
  const { data: userData } = useGetUserData();
  const userPreferences = userData?.userPreferences;
  const vehicleGroupIds = userPreferences?.vehicleGroupIds;

  const createVehicle = useMutation<NewVehicleForm, unknown, NewVehicleForm | undefined>({
    mutationFn: async (vehicle) => getFuscaVehiclesRequester().createVehicle(vehicle),
    onError: () => {
      Alert.error('Erro ao criar veículo!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
  });

  const createNewVehicle = useMutation<UUID, unknown, Vehicle>({
    mutationFn: async (vehicle: Vehicle) => getVehicleGraphQLRequester().createVehicle(vehicle),
    onError: () => {
      Alert.error('Erro ao criar veículo!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
  });

  const deleteVehicle = useMutation<void, unknown, UUID, { previousVehicles?: Vehicle[] }>({
    mutationFn: (vehicleId: UUID) => getFuscaVehiclesRequester().deleteVehicle(vehicleId),
    onSuccess: () => {
      Alert.success('Veículo removido com sucesso!');
    },
    onError: () => {
      Alert.error('Erro ao deletar veículo!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
  });

  const removeDevice = useMutation<void, unknown, UUID>({
    mutationFn: (vehicleId: UUID) => getFuscaVehiclesRequester().removeDevice(vehicleId),
    onSuccess: () => {
      Alert.success('Dispositivo desassociado com sucesso!');
    },
    onError: () => {
      Alert.error('Erro ao desassociar dispositivo!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
  });

  const associateDevice = useMutation<void, unknown, { vehicleId: UUID; deviceId: string }>({
    mutationFn: (variables) => getFuscaVehiclesRequester().associateDevice(variables.vehicleId, variables.deviceId),
    onSuccess: () => {
      Alert.success('Dispositivo associado com sucesso!');
    },
    onError: () => {
      Alert.error('Erro ao associar dispositivo!');
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
  });

  const updateVehicle = useMutation({
    mutationFn: getVehicleGraphQLRequester().saveVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [DEVICES_QUERY_KEY, true, true, true, false, vehicleGroupIds],
      });
      Alert.success('Veículo salvo com sucesso!');
    },
    onError: () => {
      Alert.error('Erro ao salvar veículo!');
    },
  });

  const createDriverAssociationRequestBatch = useMutation<void, unknown, { vehicleId: UUID; associationsRequests: AssociationPastRequest[] }>({
    mutationFn: (variables) => getFuscaVehiclesRequester().createDriverAssociationRequestBatch(variables.vehicleId, variables.associationsRequests),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['VEHICLE_PAST_ASSOCIATIONS'],
      });

      queryClient.invalidateQueries({
        queryKey: ['PATH_ASSOCIATION', 'paths'],
      });
    },
  });

  return {
    createVehicle,
    createNewVehicle,
    deleteVehicle,
    removeDevice,
    associateDevice,
    updateVehicle,
    createDriverAssociationRequestBatch,
  };
};

export default useFuscaVehicle;
